/**
 * Smart Queue entry point.
 * Route: /learn  (bottom-nav center Learn button, Weak Spots fallback)
 *
 * One request (ADR-103 Decision 1): POST /smart-queue/session/ is the stream's start.
 * It returns today's live stream session when there is one (is_resumed) and builds a
 * new one otherwise, so there is nothing for this page to look up first. It used to
 * ask GET /sessions/active (source-blind) — a read that completed every session with
 * no graduated card, of every source, which is how a learner lost their place.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import { useLocale } from '../i18n/context'
import type { SmartQueueSessionResponse } from '../types'

type Phase = 'loading' | 'empty' | 'error'

export default function SmartQueuePage() {
  const { tr } = useLocale()
  const sq = tr.smartQueue
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('loading')

  useEffect(() => {
    let cancelled = false

    async function run() {
      try {
        const sqRes = await api.post<SmartQueueSessionResponse>('/api/v1/smart-queue/session/')
        if (cancelled) return

        const { session_id, total_cards, is_resumed, resolution_count, day_boundary_closed_previous } = sqRes.data
        if (!session_id || total_cards === 0) {
          setPhase('empty')
          return
        }
        navigate('/session', {
          state: {
            sessionId: session_id,
            totalCards: total_cards,
            // Flicker-free counter placeholder on a resume (ADR-046); the Learn page's
            // authoritative status fetch corrects it.
            ...(is_resumed ? { resolutionCards: resolution_count } : {}),
            dayBoundaryClosed: day_boundary_closed_previous,
            from: '/home',
          },
          replace: true,
        })
      } catch {
        if (!cancelled) setPhase('error')
      }
    }

    void run()
    return () => { cancelled = true }
  }, [navigate])

  // ── Loading ────────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg-page)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>
          {sq.building}
        </p>
      </div>
    )
  }

  // ── Empty (no cards due today) ─────────────────────────────────────────────

  if (phase === 'empty') {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg-page)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          padding: '0 24px',
        }}
      >
        <div style={{ fontSize: 48 }}>✅</div>
        <div style={{ textAlign: 'center' }}>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: '0 0 8px',
            }}
          >
            {sq.allCaughtUp}
          </h2>
          <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: 0 }}>
            {sq.noDueDesc}
          </p>
        </div>
        <button
          onClick={() => navigate('/home')}
          style={{
            padding: '12px 28px',
            borderRadius: 50,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          {sq.home}
        </button>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: '0 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 44 }}>⚠️</div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxWidth: 320,
        }}
      >
        <p
          style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}
        >
          {sq.error}
        </p>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
          {sq.errorDesc}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => navigate('/home')}
          style={{
            padding: '12px 20px',
            borderRadius: 50,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 14,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          {sq.home}
        </button>
        <button
          onClick={() => navigate(0)} // reload this route to retry
          style={{
            padding: '12px 24px',
            borderRadius: 50,
            border: 'none',
            background: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          {sq.retry}
        </button>
      </div>
    </div>
  )
}
